import React, { useState, useEffect } from "react";
import {
  HiOutlineBookOpen,
  HiOutlineDocumentText,
  HiOutlineClock,
  HiOutlineFire,
  HiOutlineUserGroup,
  HiOutlineGlobeAlt,
  HiOutlineTag,
  HiOutlinePhoto,
  HiOutlineVideoCamera,
  HiOutlineXMark,
  HiOutlineCheck,
  HiOutlineCloudArrowUp,
} from "react-icons/hi2";
import recipeService from "../../services/RecipeService.js";
import apiConfig from "../../config/api.js";
import { countries } from "../../pages/user/dashboard/utils/countries";
import { recipeTypes } from "../../pages/user/dashboard/utils/recipeTypes";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Composant d'édition de recette
 * Formulaire moderne pour modifier une recette existante
 * Peut recevoir recipe directement ou recipeId pour charger la recette dans le popup
 */
const EditRecipe = ({
  recipe: recipeProp,
  recipeId,
  onClose,
  onRecipeUpdated,
}) => {
  const { t } = useLanguage();
  const [recipe, setRecipe] = useState(recipeProp ?? null);
  const [loadingRecipe, setLoadingRecipe] = useState(!!recipeId && !recipeProp);
  const [loadError, setLoadError] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    ingredients: "",
    instructions: "",
    prep_time: "",
    cook_time: "",
    servings: "",
    difficulty: "Facile",
    country: "",
    recipe_type: "",
    image_url: "",
    video_url: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /** Charger la recette par ID quand on ouvre le popup via openEditPopupById */
  useEffect(() => {
    if (!recipeId || recipe) return;
    const token = localStorage.getItem("token");
    if (!token) {
      setLoadError("Authentification requise");
      setLoadingRecipe(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${apiConfig.baseURL}/recipes/${recipeId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (cancelled) return;
        if (!response.ok) {
          const err = await response.json();
          setLoadError(err?.error || "Recette non trouvée");
          setLoadingRecipe(false);
          return;
        }
        const data = await response.json();
        const recipeData = data.data || data;
        setRecipe(recipeData);
        setLoadError(null);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message || "Erreur lors du chargement");
        }
      } finally {
        if (!cancelled) setLoadingRecipe(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recipeId, recipe]);

  /** Synchroniser recipe depuis les props si fourni */
  useEffect(() => {
    if (recipeProp) setRecipe(recipeProp);
  }, [recipeProp]);

  /**
   * Initialiser le formulaire avec les données de la recette
   */
  useEffect(() => {
    if (recipe) {
      setFormData({
        title: recipe.title || "",
        description: recipe.description || "",
        ingredients: recipe.ingredients || "",
        instructions: recipe.instructions || "",
        prep_time: recipe.prep_time || "",
        cook_time: recipe.cook_time || "",
        servings: recipe.servings || "",
        difficulty: recipe.difficulty || "Facile",
        country: recipe.country || "",
        recipe_type: recipe.recipe_type || "",
        image_url: recipe.image_url || "",
        video_url: recipe.video_url || "",
      });
      setImagePreview(recipe.image_url || null);
    }
  }, [recipe]);

  /**
   * Gérer les changements dans le formulaire
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "image_url") setImagePreview(value || null);
  };

  /**
   * Téléchargement d'une image (fichier → base64)
   */
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert(t("pleaseSelectValidImage"));
      return;
    }
    setUploadingImage(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setImagePreview(base64String);
      setFormData((prev) => ({ ...prev, image_url: base64String }));
      setUploadingImage(false);
    };
    reader.onerror = () => {
      alert(t("errorReadingImage"));
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  /**
   * Supprimer l'image
   */
  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, image_url: "" }));
  };

  /**
   * Gérer la soumission du formulaire
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validation basique
      if (!formData.title.trim()) {
        throw new Error("Le titre est obligatoire");
      }
      if (!formData.ingredients.trim()) {
        throw new Error("Les ingrédients sont obligatoires");
      }
      if (!formData.instructions.trim()) {
        throw new Error("Les instructions sont obligatoires");
      }

      const recipeData = {
        ...formData,
        prep_time: formData.prep_time ? parseInt(formData.prep_time) : null,
        cook_time: formData.cook_time ? parseInt(formData.cook_time) : null,
        servings: formData.servings ? parseInt(formData.servings) : null,
        image_url: formData.image_url || null,
        video_url: formData.video_url || null,
      };

      // Appel API pour mettre à jour
      await recipeService.updateRecipe(recipe.id, recipeData);

      // Succès
      onRecipeUpdated();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      setError(error.message || "Erreur lors de la mise à jour de la recette");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gérer l'annulation
   */
  const handleCancel = () => {
    onClose();
  };

  if (!recipe && !recipeId) {
    return null;
  }

  const inputClass =
    "w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500 text-slate-800 dark:text-slate-100 placeholder-slate-400 text-sm transition-all";
  const labelClass =
    "block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2";

  if (loadingRecipe || (recipeId && !recipe && !loadError)) {
    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-8 flex flex-col items-center gap-4 border border-slate-200 dark:border-slate-600"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-12 h-12 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin" />
          <p className="text-slate-600 dark:text-slate-300 font-medium">
            Chargement de la recette...
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 flex flex-col items-center gap-4 border border-slate-200 dark:border-slate-600"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-red-600 dark:text-red-400 font-medium text-center">
            {loadError}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-800 dark:text-white rounded-xl font-medium transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]"
      onClick={(e) => e.target === e.currentTarget && handleCancel()}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-600"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white p-5 sm:p-6 rounded-t-2xl">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <HiOutlineBookOpen className="w-6 h-6" />
              </span>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold">
                  Modifier la recette
                </h2>
                <p className="text-orange-100 mt-0.5 text-sm">
                  Mettez à jour les informations de votre recette
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl text-white/90 hover:text-white hover:bg-white/20 transition-all"
              aria-label="Fermer"
            >
              <HiOutlineXMark className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Formulaire */}
        <form
          onSubmit={handleSubmit}
          className="p-5 sm:p-6 space-y-6 sm:space-y-8"
        >
          {error && (
            <div className="flex items-center gap-3 bg-red-500/15 border border-red-400/40 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl">
              <HiOutlineXMark className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Présentation */}
          <section className="space-y-4">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <HiOutlineDocumentText className="w-5 h-5 text-orange-500" />
              Présentation
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Titre de la recette *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Ex: Pâtes carbonara"
                />
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className={inputClass + " resize-y min-h-[80px]"}
                  placeholder="Décrivez votre recette..."
                />
              </div>
            </div>
          </section>

          {/* Recette */}
          <section className="space-y-4">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <HiOutlineDocumentText className="w-5 h-5 text-orange-500" />
              Recette
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Ingrédients * (un par ligne)
                </label>
                <textarea
                  name="ingredients"
                  value={formData.ingredients}
                  onChange={handleChange}
                  required
                  rows={6}
                  className={inputClass + " font-mono text-sm resize-y"}
                  placeholder="200g de pâtes, 2 œufs..."
                />
              </div>
              <div>
                <label className={labelClass}>
                  Instructions * (une étape par ligne)
                </label>
                <textarea
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleChange}
                  required
                  rows={6}
                  className={inputClass + " resize-y"}
                  placeholder="1. Faire cuire les pâtes..."
                />
              </div>
            </div>
          </section>

          {/* Temps & Portions */}
          <section className="space-y-4">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <HiOutlineClock className="w-5 h-5 text-orange-500" />
              Temps & portions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>
                  <HiOutlineClock className="w-4 h-4" /> Préparation (min)
                </label>
                <input
                  type="number"
                  name="prep_time"
                  value={formData.prep_time}
                  onChange={handleChange}
                  min="0"
                  className={inputClass}
                  placeholder="15"
                />
              </div>
              <div>
                <label className={labelClass}>
                  <HiOutlineFire className="w-4 h-4" /> Cuisson (min)
                </label>
                <input
                  type="number"
                  name="cook_time"
                  value={formData.cook_time}
                  onChange={handleChange}
                  min="0"
                  className={inputClass}
                  placeholder="20"
                />
              </div>
              <div>
                <label className={labelClass}>
                  <HiOutlineUserGroup className="w-4 h-4" /> Portions
                </label>
                <input
                  type="number"
                  name="servings"
                  value={formData.servings}
                  onChange={handleChange}
                  min="1"
                  className={inputClass}
                  placeholder="4"
                />
              </div>
            </div>
          </section>

          {/* Classification */}
          <section className="space-y-4">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <HiOutlineGlobeAlt className="w-5 h-5 text-orange-500" />
              Classification
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Pays d'origine</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={inputClass + " cursor-pointer"}
                >
                  {countries.map((c) => (
                    <option key={c.code || "all"} value={c.code}>
                      {c.code ? t(c.nameKey) : "— Non spécifié —"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  <HiOutlineTag className="w-4 h-4" /> Type
                </label>
                <select
                  name="recipe_type"
                  value={formData.recipe_type}
                  onChange={handleChange}
                  className={inputClass + " cursor-pointer"}
                >
                  {recipeTypes.map((rt) => (
                    <option key={rt.value} value={rt.value}>
                      {t(rt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Difficulté</label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                  className={inputClass + " cursor-pointer"}
                >
                  <option value="Facile">Facile</option>
                  <option value="Moyen">Moyen</option>
                  <option value="Difficile">Difficile</option>
                </select>
              </div>
            </div>
          </section>

          {/* Médias */}
          <section className="space-y-4">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <HiOutlinePhoto className="w-5 h-5 text-orange-500" />
              Médias (optionnel)
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className={labelClass}>Photo</label>
                {!imagePreview ? (
                  <>
                    <label className="block cursor-pointer">
                      <div className="border-2 border-dashed border-orange-400/60 rounded-xl p-6 hover:border-orange-500 hover:bg-orange-50/30 dark:hover:bg-orange-500/10 transition-all text-center">
                        <HiOutlineCloudArrowUp className="w-10 h-10 text-orange-500/80 mx-auto mb-2" />
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {uploadingImage
                            ? "Chargement..."
                            : "Cliquez ou glissez une image"}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          PNG, JPG, GIF
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                    <p className="text-xs text-slate-500 text-center">ou</p>
                    <input
                      type="text"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleChange}
                      className={inputClass + " text-sm"}
                      placeholder="https://exemple.com/image.jpg"
                    />
                  </>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-600">
                    <img
                      src={imagePreview}
                      alt="Aperçu"
                      className="w-full h-40 object-cover"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/400x300?text=Erreur";
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-lg"
                      title="Supprimer"
                    >
                      <HiOutlineXMark className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <label className={labelClass}>
                  <HiOutlineVideoCamera className="w-4 h-4" />
                  Lien vidéo
                </label>
                <input
                  type="url"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            </div>
          </section>

          {/* Boutons */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t border-slate-200 dark:border-slate-600">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-5 py-3.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl font-semibold transition-all disabled:opacity-50"
              disabled={loading}
            >
              {t("cancel") || "Annuler"}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-5 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-orange-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  Mise à jour...
                </>
              ) : (
                <>
                  <HiOutlineCheck className="w-5 h-5" />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRecipe;
